import { useEffect, useState } from 'react';
import { get } from '../api';
import { applyBusinessTheme, businessLogo } from '../businessTheme';
import { setLocaleForCountry } from '../dates';
import { rememberBusiness } from '../lastBusiness';
import { setCurrency } from '../money';

/** A lowercase "t" drawn as a circuit trace, its two ends landing on nodes: small, useful technology passing work along. */
// Both nodes sit in one column, the crossbar's right arm matches the foot, the corner is a true
// arc, and the letter is centred optically in the badge (its heavier right side pulls it left).
const TRACE = 'M27 14.5V38A8.5 8.5 0 0 0 35.5 46.5H42M18 25.5H42';
const NODES = [
  [42, 25.5],
  [42, 46.5],
];

/**
 * The Teknika mark: the trace in Spring Bud with Android Green nodes on a Black
 * Chocolate circular badge. `fixed` keeps it Teknika CRM's own colours even when
 * a business's theme has repainted `--brand` for its own name beside it (see `PoweredBy`).
 */
export function TeknikaMark(props: { size?: string; fixed?: boolean }) {
  return (
    <svg
      className="teknika-mark"
      viewBox="0 0 64 64"
      width={props.size ?? '1.9rem'}
      height={props.size ?? '1.9rem'}
      aria-hidden="true"
      focusable="false"
    >
      <circle
        cx="32"
        cy="32"
        r="32"
        style={{ fill: props.fixed ? '#1d3309' : 'var(--brand, #1d3309)' }}
      />
      <path
        d={TRACE}
        fill="none"
        strokeWidth="5.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ stroke: props.fixed ? '#c7da91' : 'var(--brand-ink, #c7da91)' }}
      />
      {NODES.map(([cx, cy]) => (
        <circle
          key={`${cx},${cy}`}
          cx={cx}
          cy={cy}
          r="5"
          style={{ fill: props.fixed ? '#a7bd40' : 'var(--brand-node, #a7bd40)' }}
        />
      ))}
    </svg>
  );
}

/** The browser tab's title: the business's own name ("Maid 2 Clean CRM"), else Teknika CRM's. */
export function setPageTitle(businessName: string | undefined) {
  const name = businessName?.trim();
  document.title = name ? `${name} CRM` : 'Teknika CRM';
}

const CHANGED = 'teknika:brand-changed';

/** Tell every header on screen to load the business's name and logo again. */
export const brandChanged = () => window.dispatchEvent(new Event(CHANGED));

interface Brand {
  name: string;
  slug: string;
  logo: string;
  currency: string;
  country: string;
}

/**
 * A business's own name and logo, looked up by its slug for its own sign-in pages
 * (`/login/:slug`, `/portal/login/:slug`); null while there is none to show.
 */
export function useSlugBrand(slug: string | undefined) {
  // Kept with the slug it belongs to, so switching business never shows the last one's name.
  const [found, setFound] = useState<{ slug: string; name: string; logo: string } | null>(null);
  useEffect(() => {
    if (!slug) return;
    let live = true;
    get<{ name: string; logo: string }>(`/api/brand/${encodeURIComponent(slug)}`)
      .then((b) => live && setFound({ slug, name: b.name, logo: b.logo }))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [slug]);
  return slug && found?.slug === slug ? { name: found.name, logo: found.logo } : null;
}

/**
 * What the business has chosen to show beside Teknika CRM: its logo, else its name.
 * `reloadKey` asks again when it changes: a shell mounted before sign-in passes who is
 * signed in, since the brand cannot be read until someone is.
 */
export function useBrand(reloadKey?: unknown) {
  const [brand, setBrand] = useState<Brand | null>(null);
  useEffect(() => {
    let live = true;
    const load = () =>
      get<Brand>('/api/brand')
        .then((b) => {
          if (!live) return;
          setBrand(b);
          setPageTitle(b.name);
          // Every amount and date on screen reads in the business's own
          // currency and locale from here on, not just this component's.
          setCurrency(b.currency);
          setLocaleForCountry(b.country);
          applyBusinessTheme(b.slug);
          // So the *next* load can paint this business's colours immediately (see index.html),
          // rather than the default Teknika green, before this same request comes back again;
          // and so signing out lands on this business's own sign-in.
          rememberBusiness(b.slug);
        })
        .catch(() => undefined);
    void load();
    window.addEventListener(CHANGED, load);
    return () => {
      live = false;
      window.removeEventListener(CHANGED, load);
    };
  }, [reloadKey]);
  return brand;
}

/**
 * The business's own identity, shown once someone is signed in: their logo, else
 * their name, else Teknika CRM's own mark so the header is never blank while it loads
 * or before a business has set one. This is the personalised front the business's
 * own people and customers see; Teknika CRM's own name appears separately, played down.
 */
export function BrandLockup(props: { className?: string }) {
  const brand = useBrand();
  const logo = brand ? businessLogo(brand.slug, brand.logo) : '';
  const hasOwn = !!brand && (logo || brand.name);
  return (
    <span className={`lockup ${props.className ?? ''}`.trim()}>
      {hasOwn ? (
        logo ? (
          <img className="lockup-logo" src={logo} alt={brand!.name} />
        ) : (
          <strong className="lockup-biz lockup-name">{brand!.name}</strong>
        )
      ) : (
        <span className="lockup-teknika">
          <TeknikaMark />
          <strong className="lockup-name">Teknika CRM</strong>
        </span>
      )}
    </span>
  );
}

/** Teknika CRM's own credit, played down and tucked out of the way. */
export function PoweredBy() {
  return (
    <span className="powered-by">
      Powered by <TeknikaMark size="1.1rem" fixed /> Teknika CRM
    </span>
  );
}
